// Shared form-field primitives used by the admin CRUD forms (Users, Materials)
// and the whole timetable authoring surface. `Field` renders a labelled control
// with an optional required marker + error; `inputClass(error)` returns the
// input/select/textarea Tailwind classes with an error state. Keep these in
// sync across every form rather than re-inlining them.

import { Children, cloneElement, isValidElement, useId } from "react";
import { useTranslation } from "react-i18next";

// The required marker for a label. The asterisk alone is decoration;
// `aria-required` on the control carries the meaning, and the sr-only text
// names it in the accessible name for readers that announce the label before
// the state.
export function RequiredMark() {
  const { t } = useTranslation();
  return (
    <>
      <span aria-hidden="true" className="text-rose-500 ml-0.5">*</span>
      <span className="sr-only"> {t("common.requiredField")}</span>
    </>
  );
}

// The label used to be a bare <label> with neither `htmlFor` nor the control
// nested inside it, which means NO association at all: every input rendered
// through this component was nameless to a screen reader, across every form in
// the product. Rather than touch ~100 call sites, the id is generated here and
// applied to the child — so a caller passing a plain <input> gets a correctly
// labelled, correctly described control for free, and one that already sets its
// own `id`/`aria-describedby` keeps it.
export function Field({ label, error, required, hint, children }) {
  const generated = useId();
  const controlId = `${generated}-control`;
  const hintId = `${generated}-hint`;
  const errorId = `${generated}-error`;

  const described = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  // Only a single element child can be given an id — anything else (a fragment,
  // a group of radios, raw text) keeps the label as a plain heading, which is
  // still no worse than before.
  const child = Children.count(children) === 1 ? Children.only(children) : null;
  const control =
    child && isValidElement(child)
      ? cloneElement(child, {
          id: child.props.id || controlId,
          "aria-describedby": child.props["aria-describedby"] || described || undefined,
          "aria-invalid": child.props["aria-invalid"] ?? (error ? true : undefined),
          "aria-required": child.props["aria-required"] ?? (required || undefined),
        })
      : children;

  return (
    <div>
      <label
        htmlFor={child && isValidElement(child) ? child.props.id || controlId : undefined}
        className="block text-xs font-semibold text-slate-600 mb-1.5"
      >
        {label}
        {required && <RequiredMark />}
      </label>
      {control}
      {hint && (
        <p id={hintId} className="mt-1 text-[11px] font-medium text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-rose-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export const inputClass = (error) =>
  `w-full px-3 py-2.5 text-sm rounded-xl border bg-slate-50 focus:bg-white outline-none transition disabled:opacity-60 ${
    error
      ? "border-rose-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
      : "border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
  }`;
